import {
  CONVIMAGE_INCLUDE_PERSONA,
  CONVIMAGE_LEGACYMODE,
  CONVIMAGE_LORASCALE,
  CONVIMAGE_MODELSTYLE_KEY,
  CONVIMAGE_NSFWMODE,
  CONVIMAGE_USER_NEGATIVE_PROMPT,
  CONVIMAGE_USER_PROMPT_SUFFIX,
} from "@next-platform/client-common-core";
import { useCallback, useEffect, useRef, useState } from "react";

import { typedLocalStorageService } from "@/utils/localStorageRe";

const DEFAULT_MODEL_STYLE = "semi-realistic";
const DEFAULT_LORA_SCALE = 0.8;

type UseConversationImageSettingsParams = {
  isAgeVerificationRequired: boolean;
  isNsfwToggleAllowed: boolean;
};

/** Image-generation settings for the conversation, persisted in local storage. */
const useConversationImageSettings = ({ isAgeVerificationRequired, isNsfwToggleAllowed }: UseConversationImageSettingsParams) => {
  //We rely on local storage until we redux persistence properly implemented
  const [stateModelStyle, setStateModelStyle] = useState(DEFAULT_MODEL_STYLE);
  const [stateIsNsfwMode, setStateIsNsfwMode] = useState(isNsfwToggleAllowed);
  const [stateIsLegacyMode, setStateIsLegacyMode] = useState(false);
  const [stateLoraScale, setStateLoraScale] = useState(DEFAULT_LORA_SCALE);
  const [stateUserPromptSuffix, setStateUserPromptSuffix] = useState(""); // User-defined prompt suffix
  const [stateUserNegativePrompt, setStateUserNegativePrompt] = useState(""); // User-defined negative prompt
  const [stateIsIncludePersona, setStateIsIncludePersona] = useState(false);
  // Read when the settings load; loading re-runs only when age verification changes.
  const refIsNsfwToggleAllowed = useRef(isNsfwToggleAllowed);
  refIsNsfwToggleAllowed.current = isNsfwToggleAllowed;

  useEffect(() => {
    async function setImageSettings() {
      setStateModelStyle((await typedLocalStorageService.getItem(CONVIMAGE_MODELSTYLE_KEY)) || DEFAULT_MODEL_STYLE);
      setStateIsNsfwMode(
        !isAgeVerificationRequired && refIsNsfwToggleAllowed.current && (await typedLocalStorageService.getItem(CONVIMAGE_NSFWMODE)) === "true",
      );
      setStateIsLegacyMode((await typedLocalStorageService.getItem(CONVIMAGE_LEGACYMODE)) === "true");
      setStateLoraScale(Number((await typedLocalStorageService.getItem(CONVIMAGE_LORASCALE)) ?? String(DEFAULT_LORA_SCALE)));
      setStateUserPromptSuffix((await typedLocalStorageService.getItem(CONVIMAGE_USER_PROMPT_SUFFIX)) || "");
      setStateUserNegativePrompt((await typedLocalStorageService.getItem(CONVIMAGE_USER_NEGATIVE_PROMPT)) || "");
      setStateIsIncludePersona((await typedLocalStorageService.getItem(CONVIMAGE_INCLUDE_PERSONA)) === "true");
    }

    void setImageSettings();
  }, [isAgeVerificationRequired]);

  const changeModelStyle = useCallback((newModelStyle: string | number) => {
    setStateModelStyle(newModelStyle.toString());
    typedLocalStorageService.setItem(CONVIMAGE_MODELSTYLE_KEY, newModelStyle.toString());
  }, []);

  const onNsfwModeChange = useCallback((isNewNsfwMode: boolean) => {
    setStateIsNsfwMode(isNewNsfwMode);
    typedLocalStorageService.setItem(CONVIMAGE_NSFWMODE, String(isNewNsfwMode));
  }, []);

  const onLegacyModeChange = useCallback((isNewLegacyMode: boolean) => {
    setStateIsLegacyMode(isNewLegacyMode);
    typedLocalStorageService.setItem(CONVIMAGE_LEGACYMODE, String(isNewLegacyMode));
  }, []);

  const onLoraScaleChange = useCallback((newLoraScale: number | number[]) => {
    const isArray = Array.isArray(newLoraScale);

    setStateLoraScale(!isArray ? newLoraScale : newLoraScale[0]);
    typedLocalStorageService.setItem(CONVIMAGE_LORASCALE, !isArray ? newLoraScale.toString() : newLoraScale[0].toString());
  }, []);

  const onUserPromptSuffixChange = useCallback((newUserPromptSuffix: string) => {
    setStateUserPromptSuffix(newUserPromptSuffix);
    typedLocalStorageService.setItem(CONVIMAGE_USER_PROMPT_SUFFIX, newUserPromptSuffix);
  }, []);

  const onUserNegativePromptChange = useCallback((newUserNegativePrompt: string) => {
    setStateUserNegativePrompt(newUserNegativePrompt);
    typedLocalStorageService.setItem(CONVIMAGE_USER_NEGATIVE_PROMPT, newUserNegativePrompt);
  }, []);

  const onIncludePersonaChange = useCallback((isNewIncludePersona: boolean) => {
    setStateIsIncludePersona(isNewIncludePersona);
    void typedLocalStorageService.setItem(CONVIMAGE_INCLUDE_PERSONA, String(isNewIncludePersona));
  }, []);

  return {
    modelStyle: stateModelStyle,
    isNsfwToggled: !isAgeVerificationRequired && stateIsNsfwMode,
    isLegacyMode: stateIsLegacyMode,
    loraScale: stateLoraScale,
    userPromptSuffix: stateUserPromptSuffix,
    userNegativePrompt: stateUserNegativePrompt,
    isIncludePersona: stateIsIncludePersona,
    changeModelStyle,
    onNsfwModeChange,
    onLegacyModeChange,
    onLoraScaleChange,
    onUserPromptSuffixChange,
    onUserNegativePromptChange,
    onIncludePersonaChange,
  };
};

export default useConversationImageSettings;
